/**
 * The WOOPF header (home v2 chrome, spec 4.1, 7, D15, D17): the Shop disclosure, the mobile menu,
 * the cart badge and the bridge to the store's cart drawer, search dialog and country pop-up.
 * Loaded eagerly by the design layer's loader (`data-wf-module` plus
 * `data-wf-module-load="eager"` on the `<header>`).
 *
 * THE ONE SANCTIONED FETCH BRIDGE (owner answer to Q1, 2026-09-24; ADR 0005). Nothing else in the
 * woopf layer depends on Fetch. The bag icon, the search icon and the globe (and the mobile menu's
 * Cart, Search and Region tiles) are real links to `routes.cart_url` / `routes.search_url` / the
 * footer's country pill (`#wf-region`); this module turns a plain left click on one into the Fetch
 * dialog through its public hook, and leaves the click to the browser whenever that hook is absent
 * or throws:
 *
 * - open the cart drawer: `window.PubSub.publish('cart-modal:open')` (PubSub from
 *   `assets/global.js`; subscribed by `<s-cart-modal id="HeaderCart">`, `assets/section-cart-modal.js`);
 * - open the search dialog: `window.PubSub.publish('search-modal:open')` (subscribed by
 *   `<s-search-modal>`, `assets/section-search-modal.js`);
 * - open the country pop-up (country, currency, language; owner, 2026-09-28, spec
 *   2026-09-28-header-region-globe-design.md): `window.PubSub.publish('country-modal:open')`
 *   (subscribed by `<s-country-modal>`, `assets/s-country-modal.js`); the globe and the Region tile
 *   are links to the footer's country pill (`#wf-region`) when the hook is absent or throws;
 * - the hook counts as present only when `window.PubSub.publish` is a function AND the dialog's
 *   element (`s-cart-modal` / `s-search-modal` / `s-country-modal`) is on the page AND its custom
 *   element is defined (an undefined element never subscribed, so a publish would be a dead click);
 * - follow the count: the drawer dispatches `cart-modal:updated` on `document` after every
 *   re-render, `detail.cartModal` being the drawer, whose `[data-cart-count]` holds the item count;
 *   on a back-forward cache restore (`pageshow` with `persisted`) the badge is refreshed from the
 *   cart JSON URL (`/cart.js`, `item_count`) instead.
 *
 * Before publishing, focus is put on a VISIBLE element for the dialog to hand it back to: the icon
 * itself on desktop, the burger when a tile in the mobile menu was pressed (the menu closes first,
 * without restoring focus, so its inert page is released before the dialog makes its own). A
 * modified click (new tab, new window) or a click another script already handled is never taken.
 *
 * THE SEARCH RESULT CARDS (owner request 2026-10-13; ADR 0005 section 10). In the search dialog a
 * result card's name and description share its text lines: under a one-line name the description
 * keeps three lines, under a two-line name two, under three lines or more one, each cut with an
 * ellipsis. CSS cannot count the lines a name wraps to, so this module marks every result card with
 * its name's line count (`data-wf-name-lines`, the mark the core writes on collection cards) and
 * the header's stylesheet clamps the description from it; an unmarked card (JavaScript off, no
 * dialog, no `MutationObserver`) is left alone. The dialog's own script
 * (`assets/section-search-modal.js`) replaces its results through `innerHTML` as the shopper types,
 * so a `MutationObserver` on the dialog binds each new card. One `ResizeObserver` per init watches
 * the names: its first call is the first measure (after the browser's layout), it re-measures on a
 * width change or a web font swap, and it lets go of a name that has left the document; without
 * one, a card is measured as it is bound. Every name is read before any card is written, so a batch
 * costs one layout. `nameLines` is a copy of the core's (`woopf-design.js`; a module imports
 * nothing), held to it by a parity test.
 *
 * THE LAUNCHERS OVER THE FIRST SECTION (Jeff, 2026-10-27: the three purple buttons "should be
 * hidden on phone mobile view when the user is still at the first section it will only appear when
 * you are already on the second section"). This module runs first on every page (eager), so it
 * marks the header `data-wf-first-in-view` while any of the page's first section is on screen; the
 * header's stylesheet hides the breed checker, the wishlist heart and the Zendesk chat below 768px
 * and on a phone held sideways (below) while the mark is there, and while the header is not ready
 * yet, so they never flash over the first screen. The first section is the first element child of `<main>` that draws a box (a
 * script, a style or an empty section wrapper draws none), and counts only when a second one
 * follows: a page of one section holds nothing back. The mark is set at init from the section's
 * box against `window.innerHeight`, then follows an `IntersectionObserver` on that section. Without
 * the observer nothing is marked and the launchers stay where they are.
 *
 * THE LAUNCHERS WHILE THE PAGE SCROLLS DOWN (the developer, 2026-10-29, from the PURRZ™ final check,
 * ported here on request: past the first section the launchers sat over the copy a shopper reads,
 * on every page). One passive `scroll` listener on the window, read once per animation frame, marks
 * the header `data-wf-scroll-down` once `window.scrollY` has moved down by more than
 * `SCROLL_TOLERANCE_PX` from where the direction last changed, and clears it once it has moved up by
 * more than that: reading down the page puts them away, a move back up brings them back. Small
 * moves either way add up against the same reference, so a page crept down a few pixels at a time
 * is marked, and a shaky thumb changes nothing. The header's stylesheet hides the same launchers
 * and the gift below 768px and on a phone held sideways (below) while the mark is there. A window
 * with no numeric `scrollY` gets no listener and no mark.
 *
 * THE HEADER ON A PHONE HELD SIDEWAYS (the developer, 2026-10-29, responsive audit item 4, "Buttons
 * + header": at 844 x 390 the announcement bar, the header and the launchers covered about 36% of
 * the screen while scrolling). On a short sideways screen the header's stylesheet slides the
 * header away while the scroll-down mark is there (the mark is kept at every width; the stylesheet
 * decides where it counts), and it is always shown at the top of the page. So, in the same frame
 * read, this module marks the header `data-wf-page-top` while the top of `<main>` is still below
 * the top of the screen: the header's own place, and the announcement bar above it, have not
 * scrolled past yet (on a page without `<main>`, only at a scroll position of 0). The mark is set at
 * init, written only when it changes, and read again on a resize (a phone turned round). The menu
 * can open over a header that is sliding away (a tap on the burger mid-slide, the Theme Editor
 * selecting a block): the stylesheet brings the header back while the menu is open, and once the
 * slide of the header's sticky wrapper (its parent) is over, that wrapper's own `transitionend`
 * places the menu again, its height cap measured from the header's bottom edge where it came to
 * rest.
 *
 * DOM CONTRACT (everything this module reads or writes; the section owns every id and label).
 * The host is the `<header>` carrying `data-wf-module`.
 *
 * Read:
 * - `[data-wf-menu-item]` - a disclosure nav item (the Shop `<li>`). Its sibling elements are the
 *   other nav items: a mouse entering one closes the panel at once.
 * - `[data-wf-menu-toggle]` - inside the item: the chevron `<button type="button">` (no
 *   `aria-haspopup`, no `role="menu"` anywhere: this is a disclosure, not a menu).
 * - `[data-wf-mega]` - inside the item: the panel, rendered `hidden`. Its first element child is
 *   measured for the viewport clamp; every `a[href]` inside it closes the panel when followed.
 * - `[data-wf-burger]` - the burger `<button type="button">` (the menu's only open and close control).
 * - `[data-wf-menu]` - the mobile menu inside the header (`role="dialog"`, no `aria-modal`). Its
 *   first `a[href], button` takes focus on open; a plain click on any `a[href]` in it closes it.
 * - `[data-wf-cart-open]` / `[data-wf-search-open]` / `[data-wf-region-open]` - the bridge
 *   triggers, real links, anywhere in the header (the desktop icon and the menu tile each carry
 *   one).
 * - `[data-wf-cart-count]` - a badge (one per cart trigger), holding `[data-wf-cart-count-num]`
 *   (the visible number, `aria-hidden`) and `[data-wf-cart-count-label]` (the spoken ", 3 items",
 *   visually hidden), which may carry `data-wf-label-one` / `data-wf-label-other` (the words for
 *   one and for several; English "item" / "items" when absent, as the section prints them).
 * - `[data-wf-cart-url]` - the first one in the header: the cart JSON URL, used verbatim
 *   (`{{ routes.cart_url }}.js`).
 * - `document.activeElement`, `document.documentElement.clientWidth`, `getBoundingClientRect()`,
 *   `offsetWidth`, `isConnected`, and `matchMedia('(min-width: 1024px)')`.
 * - The search dialog (ADR 0005 section 10): the first `s-search-modal` element in `document`,
 *   looked up once per init; inside it every `.s-search-card` (a result card, Fetch's
 *   `snippets/c-search-product-card.liquid`) and that card's first `.s-search-card__title` (its
 *   name), whose `getBoundingClientRect().height` over its computed `lineHeight` (px) is the count.
 * - On `window`: `MutationObserver` (without it no card is marked) and `ResizeObserver` (without it
 *   a card is measured once, as it is bound), and `getComputedStyle`.
 * - The first `<main>` in `document` (`main`), its element children's tag and
 *   `getBoundingClientRect()`, `window.innerHeight` and `IntersectionObserver` (the launchers over
 *   the first section, above).
 * - `window.scrollY`, on each animation frame after a `scroll` event (the launchers while the page
 *   scrolls down, above), and in that same frame the first `<main>`'s `getBoundingClientRect().top`,
 *   read again on a resize (the page-top mark, above).
 * - The host's parent element (Shopify's section wrapper, the sticky box the stylesheet slides):
 *   its own `transitionend`, never one bubbling up from inside the header.
 *
 * Written:
 * - the toggle's `aria-expanded`, the item's `data-wf-open`, the panel's `hidden`, and the panel's
 *   `--wf-mega-shift` (px, the viewport clamp its CSS adds to `translate`);
 * - the burger's `aria-expanded`, the menu's `data-wf-open` and `--wf-menu-top` (px, the header's
 *   bottom edge), `<html data-wf-scroll-lock>`, and `inert` on every element outside the header
 *   (released on close: only the nodes this module set, never one some other script made inert);
 * - each badge's `hidden`, its number's and its label's text;
 * - `data-wf-name-lines` on each `.s-search-card` in the search dialog: its name's line count (1 or
 *   more), removed when there is nothing to count (no box, a line height that is not a length) and,
 *   on cleanup, from every card this init bound that is still in the dialog.
 * - `data-wf-first-in-view` on the host while any of the page's first section is on screen (none
 *   on a page of one section, or without `IntersectionObserver`). Removed on cleanup.
 * - `data-wf-scroll-down` on the host once the page has moved down by more than the tolerance;
 *   removed once it has moved up by more than it, and on cleanup.
 * - `data-wf-page-top` on the host while the top of `<main>` is below the top of the screen (only
 *   where `scrollY` is a number, like the scroll-down mark); written only when it changes, removed
 *   on cleanup.
 * - `data-wf-ready` on the host, last: it switches off the CSS hover fallback, and the stylesheet's
 *   hold on the launchers while the header is not running. Removed on cleanup.
 * - Focus: the first menu control (one frame after open), the burger (on close, and before a tile
 *   opens a dialog), the chevron (Escape from inside the panel), a desktop icon (before its dialog).
 *
 * No class is written, and the only classes read are the search dialog's result card and its name
 * (above). The drawer also keeps `aria-expanded` on `#HeaderCartTrigger` in step and uses it as its
 * focus fallback; that id is the section's choice, never looked up here.
 *
 * Behaviour: the panel opens on a desktop mouse hover and closes 160ms after the pointer leaves
 * (at once when another nav item is entered, never while focus is inside it); the chevron toggles
 * it by click, Enter and Space, and a mouse click on a hover-opened panel keeps it open instead of
 * snapping it shut; Escape closes it and returns focus to the chevron; it never opens on focus
 * alone. The menu below 1024px is a non-modal dialog: the page outside the header is inert and
 * scroll-locked while it is open; the burger, Escape, a followed link, a tap outside the header,
 * reaching desktop width or a back-forward cache restore close it. The Theme Editor opens the
 * panel (desktop) or the menu (below it) for a selected block and closes them on deselect.
 *
 * Module contract (the loader in `woopf-design.js`): `init(section, api)` returns the cleanup,
 * which removes every listener, timer, frame and observer it added and every mark it wrote, and
 * releases the page. No work at import time, no global, no `console`, no import. Timers through
 * `api.later` / `api.cancel`.
 *
 * Unit tests: `tests/assets/woopf-header.test.mjs`.
 */

/** Hover intent: the panel closes this long after the pointer leaves the Shop item. */
export const MENU_CLOSE_DELAY_MS = 160;
/** Where the desktop row replaces the burger (the prototype's max-width: 1023px). */
export const DESKTOP_QUERY = '(min-width: 1024px)';
/** The dropdown never comes closer than this to a viewport edge. */
export const MEGA_EDGE_PX = 16;
/** The badge shows at most this number, then "99+". */
export const CART_BADGE_MAX = 99;

export const MENU_ITEM_SELECTOR = '[data-wf-menu-item]';
export const MENU_TOGGLE_SELECTOR = '[data-wf-menu-toggle]';
export const MEGA_SELECTOR = '[data-wf-mega]';
export const BURGER_SELECTOR = '[data-wf-burger]';
export const MOBILE_MENU_SELECTOR = '[data-wf-menu]';
export const CART_OPEN_SELECTOR = '[data-wf-cart-open]';
export const SEARCH_OPEN_SELECTOR = '[data-wf-search-open]';
export const REGION_OPEN_SELECTOR = '[data-wf-region-open]';
export const CART_COUNT_SELECTOR = '[data-wf-cart-count]';
export const CART_COUNT_NUM_SELECTOR = '[data-wf-cart-count-num]';
export const CART_COUNT_LABEL_SELECTOR = '[data-wf-cart-count-label]';
export const CART_URL_ATTRIBUTE = 'data-wf-cart-url';
export const LABEL_ONE_ATTRIBUTE = 'data-wf-label-one';
export const LABEL_OTHER_ATTRIBUTE = 'data-wf-label-other';
export const OPEN_ATTRIBUTE = 'data-wf-open';
export const READY_ATTRIBUTE = 'data-wf-ready';
export const SCROLL_LOCK_ATTRIBUTE = 'data-wf-scroll-lock';
export const MEGA_SHIFT_PROPERTY = '--wf-mega-shift';
export const MENU_TOP_PROPERTY = '--wf-menu-top';

/** The Fetch side of the bridge (ADR 0005): each dialog's element and the topic that opens it. */
export const FETCH_DIALOGS = Object.freeze({
  cart: Object.freeze({ tag: 's-cart-modal', topic: 'cart-modal:open' }),
  search: Object.freeze({ tag: 's-search-modal', topic: 'search-modal:open' }),
  region: Object.freeze({ tag: 's-country-modal', topic: 'country-modal:open' }),
});
/** Dispatched on `document` by the drawer after each re-render, `detail.cartModal` = the drawer. */
export const CART_UPDATED_EVENT = 'cart-modal:updated';
/** Where the drawer prints the item count. */
export const FETCH_CART_COUNT_SELECTOR = '[data-cart-count]';
/** The search dialog's result card and its name (ADR 0005 section 10): read, never styled here. */
export const SEARCH_CARD_SELECTOR = '.s-search-card';
export const SEARCH_CARD_NAME_SELECTOR = '.s-search-card__title';
/** A result card's name line count; the core (`woopf-design.js`) writes it on collection cards. */
export const NAME_LINES_ATTRIBUTE = 'data-wf-name-lines';

export const MAIN_SELECTOR = 'main';
export const FIRST_IN_VIEW_ATTRIBUTE = 'data-wf-first-in-view';
export const SCROLL_DOWN_ATTRIBUTE = 'data-wf-scroll-down';
/** Pixels the page must move one way, from where the direction last changed, to flip the mark. */
export const SCROLL_TOLERANCE_PX = 12;
/** On the header while the top of `<main>` is below the top of the screen (responsive audit item 4). */
export const PAGE_TOP_ATTRIBUTE = 'data-wf-page-top';

const FETCH_CART_COUNT_ATTRIBUTE = 'data-cart-count';
const DEFAULT_COUNT_WORDS = Object.freeze({ one: 'item', other: 'items' });
const COUNT_LABEL_SEPARATOR = ', ';
const MENU_FIRST_CONTROL = 'a[href], button';
const LINK_SELECTOR = 'a[href]';
const SKIP_TAGS = /^(?:SCRIPT|STYLE|LINK|TEMPLATE|NOSCRIPT)$/i;

/** Headers this module drives, so a second `init` over the same element binds nothing. */
const bound = new WeakSet();

const view = () => (typeof window === 'undefined' ? null : window);

const page = () => (typeof document === 'undefined' ? null : document);

const within = (container, node) =>
  Boolean(container) &&
  Boolean(node) &&
  (container === node || container.contains?.(node) === true);

const all = (root, selector) =>
  root && typeof root.querySelectorAll === 'function' ? [...root.querySelectorAll(selector)] : [];

const focusOn = (element) => {
  if (element && typeof element.focus === 'function') element.focus({ preventScroll: true });
};

/**
 * Horizontal correction, in px, for a panel centred under its anchor.
 *
 * @param {{ anchorCenter: number, panelWidth: number, viewportWidth: number, edge?: number }} box
 * @returns {number} 0 when it fits (or cannot be measured); positive moves right, negative left.
 *   A panel wider than the viewport minus both edges is centred on the viewport.
 */
export const megaShift = ({ anchorCenter, panelWidth, viewportWidth, edge = MEGA_EDGE_PX }) => {
  if (![anchorCenter, panelWidth, viewportWidth, edge].every(Number.isFinite)) return 0;
  if (panelWidth <= 0 || viewportWidth <= 0) return 0;
  if (panelWidth + edge * 2 >= viewportWidth) return viewportWidth / 2 - anchorCenter;
  const left = anchorCenter - panelWidth / 2;
  const right = anchorCenter + panelWidth / 2;
  if (left < edge) return edge - left;
  if (right > viewportWidth - edge) return viewportWidth - edge - right;
  return 0;
};

/**
 * What the badge shows for a count: hidden at 0, the number up to 99 then "99+", and the spoken
 * label with the real count.
 *
 * @param {unknown} count
 * @param {{ one?: string, other?: string }} [words] From the markup; English when blank.
 * @returns {{ hidden: boolean, text: string, label: string }}
 */
export const cartCountView = (count, words = DEFAULT_COUNT_WORDS) => {
  const value = Number(count);
  const n = Number.isFinite(value) && value > 0 ? Math.floor(value) : 0;
  const word =
    n === 1 ? words?.one || DEFAULT_COUNT_WORDS.one : words?.other || DEFAULT_COUNT_WORDS.other;
  return {
    hidden: n === 0,
    text: n > CART_BADGE_MAX ? `${CART_BADGE_MAX}+` : String(n),
    label: `${n} ${word}`,
  };
};

/**
 * The item count the Fetch drawer rendered, from its `[data-cart-count]` (on `root` or inside it).
 *
 * @param {unknown} root The drawer (`cart-modal:updated`'s `detail.cartModal`).
 * @returns {number|null} A whole count, or null when absent or not a count.
 */
export const readCartCount = (root) => {
  if (!root || typeof root !== 'object') return null;
  const source =
    typeof root.hasAttribute === 'function' && root.hasAttribute(FETCH_CART_COUNT_ATTRIBUTE)
      ? root
      : (root.querySelector?.(FETCH_CART_COUNT_SELECTOR) ?? null);
  const raw = source?.getAttribute?.(FETCH_CART_COUNT_ATTRIBUTE);
  if (raw === null || raw === undefined || String(raw).trim() === '') return null;
  const n = Number(raw);
  return Number.isInteger(n) && n >= 0 ? n : null;
};

/**
 * How many lines a name wraps to: its box height over its line height, rounded, at least one.
 * Zero when there is nothing to count (no box, or a line height that is not a length). A copy of
 * the core's `nameLines` (`woopf-design.js`; a module imports nothing), held to it by a test.
 *
 * @param {number} height
 * @param {number} lineHeight
 * @returns {number}
 */
export const nameLines = (height, lineHeight) =>
  height > 0 && lineHeight > 0 ? Math.max(1, Math.round(height / lineHeight)) : 0;

/**
 * The page's first section: the first element child of `main` that draws a box (not a script, a
 * style or an empty section wrapper), and only when a second one follows it.
 *
 * @param {Element|null} main
 * @returns {Element|null}
 */
export const firstSection = (main) => {
  const drawn = Array.from(main?.children ?? []).filter(
    (child) =>
      !SKIP_TAGS.test(child.tagName ?? '') && Number(child.getBoundingClientRect?.()?.height) > 0,
  );
  return drawn.length >= 2 ? drawn[0] : null;
};

/**
 * Everything to make inert while the mobile menu is open: walking from `start` up to (not
 * including) `stop`, every sibling element not on the path, except SCRIPT, STYLE, LINK, TEMPLATE,
 * NOSCRIPT and anything already inert (so a release never clears an inert someone else set).
 *
 * @param {Element|null} start The header: it and its ancestors stay usable.
 * @param {Element|null} stop Usually `document.body`.
 * @returns {Element[]}
 */
export const inertTargets = (start, stop) => {
  const targets = [];
  for (let node = start; node && node !== stop; node = node.parentElement) {
    const parent = node.parentElement;
    if (!parent) break;
    Array.from(parent.children ?? []).forEach((child) => {
      if (child === node || child.inert === true || SKIP_TAGS.test(child.tagName ?? '')) return;
      targets.push(child);
    });
  }
  return targets;
};

/**
 * Whether the Fetch dialog can take the request: 'modal' when `scope.PubSub.publish` is a function
 * and the dialog's element is on the page with its custom element defined, 'navigate' otherwise
 * (the link does its job). Never throws.
 *
 * @param {{ PubSub?: { publish?: Function }, document?: Document, customElements?: CustomElementRegistry } | null} scope
 * @param {'cart'|'search'|'region'} kind
 * @returns {'modal'|'navigate'}
 */
export const modalRoute = (scope, kind) => {
  try {
    const dialog = Object.prototype.hasOwnProperty.call(FETCH_DIALOGS, kind)
      ? FETCH_DIALOGS[kind]
      : null;
    if (!dialog || !scope) return 'navigate';
    if (typeof scope.PubSub?.publish !== 'function') return 'navigate';
    const element = scope.document?.querySelector?.(dialog.tag) ?? null;
    if (!element || element.isConnected === false) return 'navigate';
    const registry = scope.customElements;
    if (registry && typeof registry.get === 'function' && !registry.get(dialog.tag)) {
      return 'navigate';
    }
    return 'modal';
  } catch {
    return 'navigate';
  }
};

/**
 * The cart's item count from its JSON URL, or null on any failure (never rejects).
 *
 * @param {string} url `{{ routes.cart_url }}.js`, from `data-wf-cart-url`.
 * @param {(url: string, init: object) => Promise<Response>} fetcher `fetch`, injected.
 * @returns {Promise<number|null>}
 */
export const fetchCartCount = async (url, fetcher) => {
  if (typeof url !== 'string' || url === '' || typeof fetcher !== 'function') return null;
  try {
    const response = await fetcher(url, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      cache: 'no-store',
    });
    if (!response?.ok) return null;
    const body = await response.json();
    const n = Number(body?.item_count);
    return Number.isInteger(n) && n >= 0 ? n : null;
  } catch {
    return null;
  }
};

/**
 * The Shop disclosure as a state machine with injected timers, so it is testable without a DOM.
 * A hover opens it unpinned (the pointer leaving closes it after `delay`); a toggle opens it
 * pinned (only a toggle, Escape, a focus or pointer leaving the item, or another item closes it).
 * A pointer click on a hover-opened panel pins it instead of closing it; a keyboard toggle always
 * flips, which is what its `aria-expanded` promised.
 *
 * @param {{ schedule: (fn: Function, ms: number) => any, cancel: (handle: any) => void,
 *           onChange: (open: boolean) => void, delay?: number, holds?: () => boolean }} deps
 *   `holds` is asked when the hover close is due: true (focus is inside) keeps the panel open.
 * @returns {{ isOpen(): boolean, isPinned(): boolean, pointerEnter(): void, pointerLeave(): void,
 *             otherItemEnter(): void, toggle(options?: { pointer?: boolean }): void, show(): void,
 *             close(): void, dispose(): void }}
 */
export const createDisclosure = ({
  schedule,
  cancel,
  onChange,
  delay = MENU_CLOSE_DELAY_MS,
  holds,
}) => {
  let open = false;
  let pinned = false;
  let timer = null;
  let disposed = false;

  const clearTimer = () => {
    if (timer === null) return;
    cancel(timer);
    timer = null;
  };

  const set = (next, pin) => {
    pinned = next && pin;
    if (open === next) return;
    open = next;
    onChange(open);
  };

  return {
    isOpen: () => open,
    isPinned: () => pinned,
    pointerEnter() {
      if (disposed) return;
      clearTimer();
      if (!open) set(true, false);
    },
    pointerLeave() {
      if (disposed || !open || pinned) return;
      clearTimer();
      timer = schedule(() => {
        timer = null;
        if (typeof holds === 'function' && holds()) return;
        if (!pinned) set(false, false);
      }, delay);
    },
    otherItemEnter() {
      clearTimer();
      set(false, false);
    },
    toggle({ pointer = false } = {}) {
      if (disposed) return;
      clearTimer();
      if (open && pointer && !pinned) {
        pinned = true;
        return;
      }
      set(!open, true);
    },
    show() {
      if (disposed) return;
      clearTimer();
      set(true, true);
    },
    close() {
      clearTimer();
      set(false, false);
    },
    dispose() {
      clearTimer();
      disposed = true;
    },
  };
};

/** A plain left click: anything else (new tab, new window, download) is left to the browser. */
const isPlainClick = (event) =>
  !event?.metaKey &&
  !event?.ctrlKey &&
  !event?.shiftKey &&
  !event?.altKey &&
  !(typeof event?.button === 'number' && event.button !== 0);

const isMouse = (event) => event?.pointerType === 'mouse';

const desktopQuery = (scope) => {
  try {
    return scope?.matchMedia?.(DESKTOP_QUERY) ?? null;
  } catch {
    return null;
  }
};

/**
 * Wire one header.
 *
 * @param {Element} section The `<header>` carrying `data-wf-module` (the loader's host).
 * @param {{ later?: (fn: Function, ms: number) => any, cancel?: (handle: any) => void }} [api]
 * @returns {() => void} The cleanup.
 */
export const init = (section, api) => {
  const header = section;
  if (!header || typeof header.querySelectorAll !== 'function' || bound.has(header)) {
    return () => {};
  }
  bound.add(header);

  const scope = view();
  const doc = page();
  const later =
    typeof api?.later === 'function'
      ? api.later
      : (callback, ms) =>
          typeof scope?.setTimeout === 'function' ? scope.setTimeout(callback, ms) : null;
  const cancel =
    typeof api?.cancel === 'function'
      ? api.cancel
      : (handle) => {
          if (handle !== null && handle !== undefined) scope?.clearTimeout?.(handle);
        };

  let disposed = false;
  const removers = [];
  const listen = (target, type, handler, options) => {
    if (!target || typeof target.addEventListener !== 'function') return;
    target.addEventListener(type, handler, options);
    removers.push(() => target.removeEventListener(type, handler, options));
  };

  const frames = new Set();
  const nextFrame = (callback) => {
    if (typeof scope?.requestAnimationFrame !== 'function') {
      callback();
      return;
    }
    let handle = null;
    handle = scope.requestAnimationFrame(() => {
      frames.delete(handle);
      if (!disposed) callback();
    });
    frames.add(handle);
  };

  const desktop = desktopQuery(scope);
  const isDesktop = () => Boolean(desktop?.matches);

  /* ---- The Shop disclosure ---- */

  const measure = (entry) => {
    if (!entry.state.isOpen()) return;
    const box = entry.item.getBoundingClientRect?.();
    const inner = entry.panel.firstElementChild ?? entry.panel;
    const shift = megaShift({
      anchorCenter: Number(box?.left) + Number(box?.width) / 2,
      panelWidth: Number(inner?.offsetWidth),
      viewportWidth: Number(doc?.documentElement?.clientWidth),
    });
    entry.panel.style?.setProperty?.(MEGA_SHIFT_PROPERTY, `${Math.round(shift)}px`);
  };

  const disclosures = all(header, MENU_ITEM_SELECTOR)
    .map((item) => {
      const toggle = item.querySelector(MENU_TOGGLE_SELECTOR);
      const panel = item.querySelector(MEGA_SELECTOR);
      if (!toggle || !panel) return null;
      const entry = { item, toggle, panel, state: null };
      entry.state = createDisclosure({
        schedule: later,
        cancel,
        holds: () => within(item, doc?.activeElement),
        onChange: (open) => {
          toggle.setAttribute('aria-expanded', String(open));
          if (open) item.setAttribute(OPEN_ATTRIBUTE, '');
          else item.removeAttribute(OPEN_ATTRIBUTE);
          panel.hidden = !open;
          if (open) nextFrame(() => measure(entry));
        },
      });
      return entry;
    })
    .filter(Boolean);

  const closeDisclosures = () => disclosures.forEach((entry) => entry.state.close());

  disclosures.forEach((entry) => {
    const { item, toggle, panel, state } = entry;
    listen(item, 'pointerenter', (event) => {
      if (isMouse(event) && isDesktop()) state.pointerEnter();
    });
    listen(item, 'pointerleave', (event) => {
      if (isMouse(event)) state.pointerLeave();
    });
    Array.from(item.parentElement?.children ?? [])
      .filter((sibling) => sibling !== item)
      .forEach((sibling) => {
        listen(sibling, 'pointerenter', (event) => {
          if (isMouse(event)) state.otherItemEnter();
        });
      });
    // Touch and pen taps reach only this path; `detail` is 0 for Enter and Space.
    listen(toggle, 'click', (event) => {
      state.toggle({ pointer: Number(event?.detail) > 0 });
    });
    // A null relatedTarget (a click on nothing focusable, the window losing focus) is left to
    // the pointerdown check, so a click on the panel's own padding does not close it.
    listen(item, 'focusout', (event) => {
      const next = event?.relatedTarget;
      if (next && !within(item, next)) state.close();
    });
    all(panel, LINK_SELECTOR).forEach((link) => listen(link, 'click', () => state.close()));
  });

  /* ---- The mobile menu ---- */

  const burger = header.querySelector(BURGER_SELECTOR);
  const menu = header.querySelector(MOBILE_MENU_SELECTOR);
  let menuOpen = false;
  let inerted = [];

  const placeMenu = () => {
    if (!menuOpen) return;
    const bottom = Number(header.getBoundingClientRect?.()?.bottom);
    const top = Number.isFinite(bottom) ? Math.max(0, Math.round(bottom)) : 0;
    menu.style?.setProperty?.(MENU_TOP_PROPERTY, `${top}px`);
  };

  // A menu opened over a header sliding away (responsive audit item 4) was placed from a moving
  // bottom edge; the slide ends on the wrapper itself (the transitions inside the header bubble here
  // too, and change nothing).
  const wrapper = header.parentElement ?? null;
  listen(wrapper, 'transitionend', (event) => {
    if (event?.target === wrapper) placeMenu();
  });

  const openMenu = ({ focus = true } = {}) => {
    if (menuOpen || disposed || !burger || !menu) return;
    // A Theme Editor reload can leave a stale menu behind: never inert the page for it.
    if (menu.isConnected === false || header.isConnected === false) return;
    menuOpen = true;
    closeDisclosures();
    burger.setAttribute('aria-expanded', 'true');
    menu.setAttribute(OPEN_ATTRIBUTE, '');
    placeMenu();
    doc?.documentElement?.setAttribute?.(SCROLL_LOCK_ATTRIBUTE, '');
    inerted = inertTargets(header, doc?.body ?? null);
    inerted.forEach((node) => {
      node.inert = true;
    });
    if (focus) {
      nextFrame(() => {
        if (menuOpen) focusOn(menu.querySelector(MENU_FIRST_CONTROL));
      });
    }
  };

  const closeMenu = ({ restoreFocus = true } = {}) => {
    if (!menuOpen) return;
    menuOpen = false;
    burger.setAttribute('aria-expanded', 'false');
    menu.removeAttribute(OPEN_ATTRIBUTE);
    doc?.documentElement?.removeAttribute?.(SCROLL_LOCK_ATTRIBUTE);
    inerted.splice(0).forEach((node) => {
      node.inert = false;
    });
    if (restoreFocus) focusOn(burger);
  };

  if (burger && menu) {
    listen(burger, 'click', () => (menuOpen ? closeMenu() : openMenu()));
    // Every link, the Cart and Search tiles included (closing twice is harmless, and the bridge
    // below then moves focus to the burger). A modified click opens a new tab and leaves the
    // reader here, so the menu stays open for it.
    all(menu, LINK_SELECTOR).forEach((link) =>
      listen(link, 'click', (event) => {
        if (isPlainClick(event)) closeMenu({ restoreFocus: false });
      }),
    );
  }

  /* ---- The cart and search bridge (ADR 0005) ---- */

  const onBridge = (event, trigger, kind) => {
    if (event?.defaultPrevented || !isPlainClick(event)) return;
    const inMenu = within(menu, trigger);
    if (modalRoute(scope, kind) !== 'modal') {
      if (inMenu) closeMenu({ restoreFocus: false });
      return;
    }
    if (inMenu) {
      closeMenu({ restoreFocus: false });
      focusOn(burger);
    } else {
      focusOn(trigger);
    }
    try {
      scope.PubSub.publish(FETCH_DIALOGS[kind].topic);
    } catch {
      return;
    }
    event.preventDefault();
  };

  [
    [CART_OPEN_SELECTOR, 'cart'],
    [SEARCH_OPEN_SELECTOR, 'search'],
    [REGION_OPEN_SELECTOR, 'region'],
  ].forEach(([selector, kind]) => {
    all(header, selector).forEach((trigger) => {
      listen(trigger, 'click', (event) => onBridge(event, trigger, kind));
    });
  });

  /* ---- The search dialog's result cards (ADR 0005 section 10) ---- */

  const searchDialog = doc?.querySelector?.(FETCH_DIALOGS.search.tag) ?? null;
  /* Per init, so a restart after cleanup binds every card again. */
  const searchCardsBound = new WeakSet();
  const cardOfName = new WeakMap();
  let cardObserver = null;
  let nameObserver = null;

  /** Every read first, then every write: one layout for a whole batch, not one per card. */
  const markNames = (names) => {
    const counts = names.map((name) =>
      nameLines(
        Number(name.getBoundingClientRect?.()?.height),
        Number.parseFloat(scope?.getComputedStyle?.(name)?.lineHeight ?? ''),
      ),
    );
    names.forEach((name, index) => {
      const card = cardOfName.get(name);
      if (!card) return;
      if (counts[index] > 0) card.setAttribute(NAME_LINES_ATTRIBUTE, String(counts[index]));
      else card.removeAttribute(NAME_LINES_ATTRIBUTE);
    });
  };

  /** The cards in the dialog not bound yet: their names observed, or measured at once. */
  const bindNewCards = () => {
    const names = [];
    all(searchDialog, SEARCH_CARD_SELECTOR).forEach((card) => {
      if (searchCardsBound.has(card)) return;
      const name = card.querySelector?.(SEARCH_CARD_NAME_SELECTOR) ?? null;
      if (!name) return;
      searchCardsBound.add(card);
      cardOfName.set(name, card);
      names.push(name);
    });
    if (nameObserver) names.forEach((name) => nameObserver.observe(name));
    else markNames(names);
  };

  const bindSearchCards = () => {
    if (!searchDialog || typeof scope?.MutationObserver !== 'function') return;
    if (typeof scope.ResizeObserver === 'function') {
      nameObserver = new scope.ResizeObserver((entries) => {
        if (disposed) return;
        const names = [];
        entries.forEach(({ target }) => {
          /* Strictly false: `isConnected` is undefined on a test double. */
          if (target.isConnected === false) nameObserver.unobserve(target);
          else names.push(target);
        });
        markNames(names);
      });
    }
    cardObserver = new scope.MutationObserver(() => {
      if (!disposed) bindNewCards();
    });
    cardObserver.observe(searchDialog, { childList: true, subtree: true });
    bindNewCards();
  };

  bindSearchCards();

  /* ---- The launchers over the first section (Jeff, 2026-10-27) ---- */

  let sectionObserver = null;
  const main = doc?.querySelector?.(MAIN_SELECTOR) ?? null;

  const markFirstInView = (onScreen) => {
    if (onScreen) header.setAttribute(FIRST_IN_VIEW_ATTRIBUTE, '');
    else header.removeAttribute(FIRST_IN_VIEW_ATTRIBUTE);
  };

  const bindFirstSection = () => {
    if (typeof scope?.IntersectionObserver !== 'function') return;
    const first = firstSection(main);
    if (!first) return;
    // At once, so the launchers never show over the first screen before the observer's first call.
    const box = first.getBoundingClientRect();
    const height = Number(scope.innerHeight);
    if (Number.isFinite(height)) markFirstInView(box.bottom > 0 && box.top < height);
    sectionObserver = new scope.IntersectionObserver((entries) => {
      const entry = entries[entries.length - 1];
      if (entry && !disposed) markFirstInView(entry.isIntersecting);
    });
    sectionObserver.observe(first);
  };

  bindFirstSection();

  /* ---- The launchers while the page scrolls down (the developer, 2026-10-29) ---- */

  let scrollReference = Number(scope?.scrollY);
  let scrollFramePending = false;
  const scrollGate = Number.isFinite(scrollReference);

  /*
   * The page-top mark (the developer, 2026-10-29, responsive audit item 4): the header is always
   * shown while the top of <main> is still below the top of the screen. Written only when it
   * changes: every write on the header re-checks the stylesheet's :has() rules keyed on it.
   */
  const markPageTop = (y) => {
    const top = Number(main?.getBoundingClientRect?.()?.top);
    const atTop = Number.isFinite(top) ? top > 0 : y <= 0;
    if (atTop === header.hasAttribute(PAGE_TOP_ATTRIBUTE)) return;
    if (atTop) header.setAttribute(PAGE_TOP_ATTRIBUTE, '');
    else header.removeAttribute(PAGE_TOP_ATTRIBUTE);
  };

  const readScroll = () => {
    scrollFramePending = false;
    const y = Number(scope.scrollY);
    if (!Number.isFinite(y)) return;
    markPageTop(y);
    const moved = y - scrollReference;
    if (moved > SCROLL_TOLERANCE_PX) {
      header.setAttribute(SCROLL_DOWN_ATTRIBUTE, '');
      scrollReference = y;
    } else if (moved < -SCROLL_TOLERANCE_PX) {
      header.removeAttribute(SCROLL_DOWN_ATTRIBUTE);
      scrollReference = y;
    }
  };

  if (scrollGate) {
    markPageTop(scrollReference);
    listen(
      scope,
      'scroll',
      () => {
        if (scrollFramePending) return;
        scrollFramePending = true;
        nextFrame(readScroll);
      },
      { passive: true },
    );
  }

  /* ---- The cart badge ---- */

  let countVersion = 0;

  const applyCount = (count) => {
    countVersion += 1;
    all(header, CART_COUNT_SELECTOR).forEach((badge) => {
      const label = badge.querySelector?.(CART_COUNT_LABEL_SELECTOR) ?? null;
      const shown = cartCountView(count, {
        one: label?.getAttribute?.(LABEL_ONE_ATTRIBUTE) ?? '',
        other: label?.getAttribute?.(LABEL_OTHER_ATTRIBUTE) ?? '',
      });
      badge.hidden = shown.hidden;
      const num = badge.querySelector?.(CART_COUNT_NUM_SELECTOR) ?? null;
      if (num) num.textContent = shown.text;
      if (label) label.textContent = `${COUNT_LABEL_SEPARATOR}${shown.label}`;
    });
  };

  const refreshCount = () => {
    const url = header.querySelector(`[${CART_URL_ATTRIBUTE}]`)?.getAttribute(CART_URL_ATTRIBUTE);
    if (!url) return;
    const version = countVersion;
    const fetcher =
      typeof scope?.fetch === 'function' ? (target, options) => scope.fetch(target, options) : null;
    fetchCartCount(url, fetcher).then((count) => {
      // Released meanwhile, or the drawer reported a newer count while this was in flight.
      if (disposed || count === null || version !== countVersion) return;
      applyCount(count);
    });
  };

  /* ---- Page-wide listeners ---- */

  listen(doc, 'keydown', (event) => {
    if (event?.key !== 'Escape' || event.defaultPrevented) return;
    if (menuOpen) {
      event.preventDefault();
      closeMenu();
      return;
    }
    const open = disclosures.filter((entry) => entry.state.isOpen());
    if (open.length === 0) return;
    event.preventDefault();
    open.forEach((entry) => {
      const hadFocus = within(entry.item, doc?.activeElement);
      entry.state.close();
      if (hadFocus) focusOn(entry.toggle);
    });
  });

  listen(doc, 'pointerdown', (event) => {
    const target = event?.target;
    disclosures.forEach((entry) => {
      if (entry.state.isOpen() && !within(entry.item, target)) entry.state.close();
    });
    if (menuOpen && !within(header, target)) closeMenu({ restoreFocus: false });
  });

  listen(doc, CART_UPDATED_EVENT, (event) => {
    const count = readCartCount(event?.detail?.cartModal);
    if (count !== null) applyCount(count);
  });

  const onResize = () => {
    placeMenu();
    disclosures.forEach((entry) => measure(entry));
    // A phone turned round moves <main> without a scroll of its own.
    const y = Number(scope?.scrollY);
    if (scrollGate && Number.isFinite(y)) markPageTop(y);
  };
  listen(scope, 'resize', onResize, { passive: true });
  listen(scope?.visualViewport, 'resize', onResize, { passive: true });

  listen(scope, 'pageshow', (event) => {
    if (!event?.persisted) return;
    // A back-forward cache restore must not come back with an inert page or a stale count.
    closeMenu({ restoreFocus: false });
    closeDisclosures();
    refreshCount();
  });

  const onViewportChange = (event) => {
    if (event?.matches) closeMenu({ restoreFocus: false });
    else closeDisclosures();
  };
  if (typeof desktop?.addEventListener === 'function') {
    listen(desktop, 'change', onViewportChange);
  } else if (typeof desktop?.addListener === 'function') {
    desktop.addListener(onViewportChange);
    removers.push(() => desktop.removeListener(onViewportChange));
  }

  /* ---- The Theme Editor ---- */

  listen(header, 'shopify:block:select', (event) => {
    const target = event?.target;
    if (!within(header, target)) return;
    if (isDesktop()) {
      disclosures.find((entry) => within(entry.item, target))?.state.show();
    } else {
      openMenu({ focus: false });
    }
  });
  listen(header, 'shopify:block:deselect', () => {
    closeDisclosures();
    closeMenu({ restoreFocus: false });
  });

  header.setAttribute(READY_ATTRIBUTE, '');

  return () => {
    if (disposed) return;
    removers.splice(0).forEach((remove) => remove());
    frames.forEach((handle) => scope?.cancelAnimationFrame?.(handle));
    frames.clear();
    closeMenu({ restoreFocus: false });
    disposed = true;
    disclosures.forEach((entry) => {
      entry.state.close();
      entry.state.dispose();
      entry.panel.style?.removeProperty?.(MEGA_SHIFT_PROPERTY);
    });
    menu?.style?.removeProperty?.(MENU_TOP_PROPERTY);
    cardObserver?.disconnect();
    nameObserver?.disconnect();
    all(searchDialog, SEARCH_CARD_SELECTOR).forEach((card) => {
      if (searchCardsBound.has(card)) card.removeAttribute(NAME_LINES_ATTRIBUTE);
    });
    sectionObserver?.disconnect();
    header.removeAttribute(FIRST_IN_VIEW_ATTRIBUTE);
    header.removeAttribute(SCROLL_DOWN_ATTRIBUTE);
    header.removeAttribute(PAGE_TOP_ATTRIBUTE);
    header.removeAttribute(READY_ATTRIBUTE);
    bound.delete(header);
  };
};
